import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { io, Socket } from "socket.io-client";
import { friendDuelAPI } from "../api";

const SOCKET_BASE =
  process.env.REACT_APP_SOCKET_URL ||
  (process.env.REACT_APP_BASE_URL || "http://localhost:3003/api").replace(
    /\/api$/,
    "",
  );

export interface IncomingInvite {
  inviteId: string;
  questionCount: number;
  expiresAt: string;
  inviterName: string;
  inviterPhoto: string;
}

interface FriendDuelContextType {
  incomingInvites: IncomingInvite[];
  removeInvite: (inviteId: string) => void;
}

const FriendDuelContext = createContext<FriendDuelContextType>({
  incomingInvites: [],
  removeInvite: () => {},
});

export const useFriendDuel = () => useContext(FriendDuelContext);

export const FriendDuelProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [incomingInvites, setIncomingInvites] = useState<IncomingInvite[]>([]);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    friendDuelAPI
      .getPending()
      .then((res) => {
        if (Array.isArray(res.data)) setIncomingInvites(res.data);
      })
      .catch(() => {});

    const socket = io(`${SOCKET_BASE}/friend-duel`, {
      auth: { token },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });
    socketRef.current = socket;

    socket.on("friend_invite_received", (data: IncomingInvite) => {
      setIncomingInvites((prev) => {
        if (prev.some((i) => i.inviteId === data.inviteId)) return prev;
        return [...prev, data];
      });
    });

    socket.on("friend_invite_cancelled", ({ inviteId }: { inviteId: string }) => {
      setIncomingInvites((prev) => prev.filter((i) => i.inviteId !== inviteId));
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  const removeInvite = (inviteId: string) => {
    setIncomingInvites((prev) => prev.filter((i) => i.inviteId !== inviteId));
  };

  return (
    <FriendDuelContext.Provider value={{ incomingInvites, removeInvite }}>
      {children}
    </FriendDuelContext.Provider>
  );
};
